"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

import YearSelect from "@/components/OOSComponents/YearSelect.tsx/YearSelect";
import SwitchButton from "@/components/ui/SwitchButton/SwitchButton";
import { IDocumentCategoriesListProps, IDocumentCategory, IDocumentYear } from "@/types";

import DocumentListing from "../DocumentListing/DocumentListing";

function filterDocumentsByYear(data: IDocumentCategory, year: number) {
  if (!year) return data;
  const filteredDocumentListing = data.documentListing.map((listing) => {
    const filteredDocuments = listing.documents.filter((doc) => {
      return doc?.year?.year === year;
    });
    return {
      ...listing,
      documents: filteredDocuments,
    };
  });

  return {
    ...data,
    documentListing: filteredDocumentListing,
  };
}

function DocumentCategories(props: IDocumentCategoriesListProps) {
  const { categories } = props.entry;
  const { category, year } = props.searchParams;
  const { replace } = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState<IDocumentCategory | null>(null);
  const [years, setYears] = useState<IDocumentYear[]>([]);

  useEffect(() => {
    if (categories) {
      setSelectedCategory(categories[0]);
    }
  }, [categories]);

  useEffect(() => {
    if (categories) {
      if (category || year) {
        const categoryFind = categories.find((cat) => cat.categoryName === category) ?? categories[0];
        if (year) {
          const filteredByYear = filterDocumentsByYear(categoryFind, Number(year));
          setSelectedCategory(filteredByYear);
        } else {
          setSelectedCategory(categoryFind);
        }
      }
    }
  }, [categories, category, year]);

  useEffect(() => {
    if (selectedCategory && categories) {
      const categorydata = categories.find((cat) => cat.categoryName === selectedCategory.categoryName);
      const allDocuments = categorydata?.documentListing.flatMap((listing) => listing.documents);
      const documentsYears = allDocuments?.map((document) => document.year);
      setYears(documentsYears || []);
    }
  }, [selectedCategory, categories]);

  const handleSelectCategory = (categoryName: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("category", categoryName);
    params.delete("year");
    replace(`${pathname}?${params.toString()}`, { scroll: false });
  };
  const handleSelectYear = (year: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("year", year.toString());
    replace(`${pathname}?${params.toString()}`, { scroll: false });
  };

  if (!selectedCategory) return null;

  return (
    <>
      <section
        className={`
                      md:container
                  `}
      >
        <h1
          className={`
                          font-primary
                          mt-l
                          mb-[40px]
                          text-center
                          text-xl
                          uppercase
                      `}
        >
          {selectedCategory.pageName}
        </h1>
        <div
          className={`
                          py-[40px]
                      `}
        >
          <div
            className={`
                  px-[4px]
                  min-h-[40px]
                  flex
                  items-center
                  justify-center
                  bg-gray-10
                  overflow-x-auto
                  w-full
                  mx-auto
                  md:w-fit
                  rounded-[40px]
              `}
          >
            {categories.map((category, idx) => (
              <SwitchButton
                key={idx}
                label={category.categoryName}
                type="switch"
                selected={category.categoryName === selectedCategory.categoryName}
                clickHandler={() => handleSelectCategory(category.categoryName)}
                customClass=""
              />
            ))}
          </div>
        </div>
        <div>
          <YearSelect years={years} clickHandler={handleSelectYear} selectedYear={Number(year)} />
        </div>
      </section>
      {selectedCategory && (
        <section
          className={`
                      md:container
                  `}
        >
          {selectedCategory.documentListing.map((listing, idx) => {
            return <DocumentListing key={idx} entry={listing} />;
          })}
        </section>
      )}
    </>
  );
}

export default DocumentCategories;
